import { get } from 'lodash';
import { Cookies } from 'react-cookie';
import moment from 'moment';
import i18n from 'i18n';
import { fetchProfile, checkLoginData } from 'actions/profile';
import { resetAllChannelsData } from 'actions/channels';
import { resetAllDetailsData } from 'actions/details';
import { resetAllWatchlistData } from 'actions/watchlist';
import { resetAllContinueWatchingData } from 'actions/continue-watching';
import { resetSearchQuery } from 'actions/search';
import { navigateToLastPlayedChannel } from 'actions/player';
import { hideLoader, showLoader } from 'actions/loader';
import { checkUserNotifications, showFirstUserNotification } from 'actions/notifications';
import { getIsLoggedInSelector, getProfileId } from 'reducers/profile/selectors';
import { selectIsAutostartTVEnabled } from 'reducers/autostart-tv/selectors';
import { selectAuthSteps, selectUILanguage } from 'reducers/app/selectors';
import { LANGUAGES, UI_LANGUAGE_STORAGE_KEY } from 'constants/languages';
import { checkConnection, wait } from './helpers';
import innerAnalyticsVolia from 'helpers/innerAnalyticsVolia';
import requester from 'utils/requester';
import api from 'config/api';
import Platform from 'platform';
import storage from 'helpers/storage';
import { hideModal, showModal } from 'actions/modal';
import { MODAL_TYPES } from 'constants/modal-types';
import * as types from './types';

const ONE_YEAR_IN_SECONDS = 60 * 60 * 24 * 365;

export const enableMouseControls = (mouseControlsEnabled) => ({
  type: types.ENABLE_MOUSE_CONTROLS,
  payload: { mouseControlsEnabled },
});

// Russian language was removed. Need to set UA by default.
export const rusLangFallback = () => (dispatch, getState) => {
  const lang = selectUILanguage(getState());
  if (lang === 'ru') dispatch(changeUILanguage(LANGUAGES.UK));
};

export const initUserData = () => async (dispatch, getState) => {
  try {
    dispatch({ type: types.INIT_USER_DATA });

    dispatch(rusLangFallback());

    const isAutoStartEnabled = selectIsAutostartTVEnabled(getState());
    const isTizen = Platform.getName() === 'Tizen';
    // const isBrowser = Platform.getName() === 'Browser';

    if (isTizen) dispatch(setOnSleepListener());

    await dispatch(fetchProfile());

    const isLoggedIn = getIsLoggedInSelector(getState());
    // Maybe all isLoggedIn checks should be inside "checkUserNotifications" action
    // And "checkUserNotifications" should show notifications
    if (isLoggedIn) {
      const notificationsData = await dispatch(checkUserNotifications());
      const notificationTitle = get(notificationsData, '[0].title', false);

      if (notificationTitle) {
        innerAnalyticsVolia.postEvent({
          eventGroup: 'notification',
          eventName: `show notification`,
          eventValue: notificationTitle,
        });
      }
      // We have to wait out for page rendering and then show notification. It's needed to handle focus properly
      setTimeout(() => dispatch(showFirstUserNotification(notificationsData)), 7000);

      dispatch(checkLoginData());
    }

    dispatch({ type: types.INIT_USER_DATA_SUCCESS });

    if (isAutoStartEnabled) dispatch(navigateToLastPlayedChannel());
  } catch (error) {
    dispatch({ type: types.INIT_USER_DATA_ERROR });
  }
};

export const navigateToAuthPage = (search) => (_, getState, history) => {
  history.push({ pathname: '/profile/authorization', search });
};

export const resetAuthSteps = () => ({ type: types.RESET_AUTH_STEPS });

export const backToPageThatCausedAuth = () => (dispatch, getState, history) => {
  const authSteps = selectAuthSteps(getState());
  if (authSteps === 0) return;
  history.go(-authSteps);
  dispatch(resetAuthSteps());
};

export const resetUserData = () => (dispatch) => {
  dispatch({ type: types.RESET_USER_DATA });

  return Promise.all([
    dispatch(resetAllChannelsData()),
    dispatch(resetAllDetailsData()),
    dispatch(resetAllWatchlistData()),
    dispatch(resetAllContinueWatchingData()),
    dispatch(resetSearchQuery()),
  ]);
};

export const changeUILanguage = (payload) => async () => {
  const cookies = new Cookies();
  cookies.set(UI_LANGUAGE_STORAGE_KEY, payload, {
    path: '/',
    maxAge: ONE_YEAR_IN_SECONDS,
  });

  window.location.hash = '';
  window.location.reload();
};

export const patchUserProfileLanguage = (language) => (dispatch, getState) => {
  const userId = getProfileId(getState());

  const data = {
    language: language.toUpperCase(),
    usePersonalData: false,
    cookiesAccepted: false,
    userSettings: {
      notificationPreferences: {
        allowNotification: true,
        specialOffers: true,
      },
      streamingPreferences: {
        subtitlesLanguage: 'UA',
        audioLanguage: 'UA',
        streamingQuality: 'AUTO',
      },
    },
  };

  dispatch(
    requester({
      method: 'patch',
      url: `${api.host}/sb/user/${userId}`,
      appendParams: true,
      data,
    }),
  );
};

export const goBackUntil = (cb) => async (dispatch, _, history) => {
  const goBack = function* (history) {
    // trigger actual location first
    yield Promise.resolve(history.location.pathname);

    while (true) {
      yield new Promise((resolve) => {
        history.goBack();
        setTimeout(() => resolve(history.location.pathname), 30);
      });
    }
  };

  for await (const path of goBack(history)) {
    if (cb(path)) break;
  }
};

const setOnSleepListener = () => (dispatch) => {
  const STORAGE_KEY = 'SLEEP_TIMESTAMP';
  // TODO change back to 6
  const MAX_HOURS_DIFF = 70;

  const onFallsAsleep = () => {
    storage.set(STORAGE_KEY, Date.now());
    dispatch(goBackUntil((path) => path.includes('player') === false));
  };

  const onWakesUp = async () => {
    const timestamp = Number(storage.get(STORAGE_KEY));
    dispatch(showLoader({ type: 'network-check' }));
    await wait(1000);

    // TODO change back to "hours"
    const diffHours = moment(Date.now()).diff(timestamp, 'minutes');
    let isOnline = false;

    try {
      isOnline = await checkConnection();
    } finally {
      storage.remove(STORAGE_KEY);
      dispatch(hideLoader());
    }

    if (!isOnline) {
      dispatch(
        showModal(MODAL_TYPES.INFO, {
          message: i18n.t('common.modal.noNetworkModalTitle'),
          buttonText: i18n.t('common.modal.noNetworkModalButton'),
          onModalBack() {
            Platform.exit();
            dispatch(hideModal());
          },
        }),
      );
    } else if (diffHours >= MAX_HOURS_DIFF) {
      Platform.forceReload();
    }
  };

  const onVisibilityChange = () => (window.document.hidden ? onFallsAsleep() : onWakesUp());

  document.addEventListener('visibilitychange', onVisibilityChange);
};

export const onLocationChange = (payload) => ({ type: types.LOCATION_CHANGE, payload });
